// src/App.tsx
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { ScrollToTop } from './components/ScrollToTop';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazily load pages that aren't needed for initial render
const Privacy = lazy(() => import('./pages/Privacy').then(module => ({ default: module.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const Disclaimer = lazy(() => import('./pages/Disclaimer').then(module => ({ default: module.Disclaimer })));
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));

// Lazy load calculator pages
const MortgageCalculator = lazy(() => import('./pages/MortgageCalculator').then(module => ({ default: module.MortgageCalculator })));
const HomeAffordabilityCalculator = lazy(() => import('./pages/HomeAffordabilityCalculator').then(module => ({ default: module.HomeAffordabilityCalculator })));
const PurchaseTimelineCalculator = lazy(() => import('./pages/PurchaseTimelineCalculator').then(module => ({ default: module.PurchaseTimelineCalculator })));
const DownPaymentCalculator = lazy(() => import('./pages/DownPaymentCalculator').then(module => ({ default: module.DownPaymentCalculator })));

// Lazy load embed tools
const EmbeddableCalculator = lazy(() => import('./pages/EmbeddableCalculator').then(module => ({ default: module.EmbeddableCalculator })));
const MortgagePaymentEmbed = lazy(() => import('./pages/MortgagePaymentEmbed').then(module => ({ default: module.MortgagePaymentEmbed })));
const HomeAffordabilityEmbed = lazy(() => import('./pages/HomeAffordabilityEmbed').then(module => ({ default: module.HomeAffordabilityEmbed })));
const PurchaseTimelineEmbed = lazy(() => import('./pages/PurchaseTimelineEmbed').then(module => ({ default: module.PurchaseTimelineEmbed })));
const DownPaymentEmbed = lazy(() => import('./pages/DownPaymentEmbed').then(module => ({ default: module.DownPaymentEmbed })));

// Simple loading component
const LoadingFallback = () => (
  <div className="flex justify-center items-center min-h-[400px]">
    <div className="animate-pulse bg-primary/10 p-8 rounded-lg text-primary font-medium">
      Loading...
    </div>
  </div>
);

// Analytics component to track page views
function Analytics() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        send_to: 'G-GRWEJCKKW2'
      });
    }
  }, [location]);
  
  return null;
}

export function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <Analytics />
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            
            {/* Calculator Routes for SEO - with code splitting */}
            <Route path="mortgage-payment-calculator" element={
              <Suspense fallback={<LoadingFallback />}>
                <MortgageCalculator />
              </Suspense>
            } />
            <Route path="home-affordability-calculator" element={
              <Suspense fallback={<LoadingFallback />}>
                <HomeAffordabilityCalculator />
              </Suspense>
            } />
            <Route path="purchase-timeline-calculator" element={
              <Suspense fallback={<LoadingFallback />}>
                <PurchaseTimelineCalculator />
              </Suspense>
            } />
            <Route path="down-payment-calculator" element={
              <Suspense fallback={<LoadingFallback />}>
                <DownPaymentCalculator />
              </Suspense>
            } />
            
            {/* About page */}
            <Route path="about" element={
              <Suspense fallback={<LoadingFallback />}>
                <About />
              </Suspense>
            } />
            
            {/* Legal pages with code splitting */}
            <Route path="privacy" element={
              <Suspense fallback={<LoadingFallback />}>
                <Privacy />
              </Suspense>
            } />
            <Route path="terms" element={
              <Suspense fallback={<LoadingFallback />}>
                <Terms />
              </Suspense>
            } />
            <Route path="disclaimer" element={
              <Suspense fallback={<LoadingFallback />}>
                <Disclaimer />
              </Suspense>
            } />
            
            {/* Embed tool pages - with code splitting */}
            <Route path="mortgage-payment-embed" element={
              <Suspense fallback={<LoadingFallback />}>
                <MortgagePaymentEmbed />
              </Suspense>
            } />
            <Route path="home-affordability-embed" element={
              <Suspense fallback={<LoadingFallback />}>
                <HomeAffordabilityEmbed />
              </Suspense>
            } />
            <Route path="purchase-timeline-embed" element={
              <Suspense fallback={<LoadingFallback />}>
                <PurchaseTimelineEmbed />
              </Suspense>
            } />
            <Route path="down-payment-embed" element={
              <Suspense fallback={<LoadingFallback />}>
                <DownPaymentEmbed />
              </Suspense>
            } />
          </Route>
          
          {/* Keep this route outside Layout since it's the actual embedded calculator */}
          <Route path="embed" element={
            <Suspense fallback={<LoadingFallback />}>
              <EmbeddableCalculator />
            </Suspense>
          } />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}